import type { ReactNode } from 'react';

interface IconProps {
  size?: number;
  /** Decorative by default; the button around it carries the label. */
  title?: string;
}

function svg(children: ReactNode, defaults: { fill?: string; strokeWidth?: number; linejoin?: 'round'; linecap?: 'round' } = {}) {
  return function Icon({ size = 22, title }: IconProps) {
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={defaults.fill ?? 'none'}
        stroke={defaults.fill ? 'none' : 'currentColor'}
        strokeWidth={defaults.strokeWidth ?? 2}
        strokeLinecap={defaults.linecap}
        strokeLinejoin={defaults.linejoin}
        aria-hidden={title ? undefined : true}
        role={title ? 'img' : undefined}
      >
        {title ? <title>{title}</title> : null}
        {children}
      </svg>
    );
  };
}

const MIC = 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z';

export const MicIcon = svg(
  <>
    <path d={MIC} />
    <path d="M19 11a7 7 0 0 1-14 0M12 18v3" />
  </>,
  { linecap: 'round' },
);
export const MicOffIcon = svg(
  <>
    <path d={MIC} />
    <path d="M19 11a7 7 0 0 1-14 0M12 18v3M3 3l18 18" />
  </>,
  { linecap: 'round' },
);
export const CamIcon = svg(
  <>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="M16 10l5-3v10l-5-3z" />
  </>,
  { linejoin: 'round' },
);
export const CamOffIcon = svg(
  <>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="M16 10l5-3v10l-5-3z" />
    <path d="M3 3l18 18" />
  </>,
  { linejoin: 'round', linecap: 'round' },
);
export const ScreenIcon = svg(
  <>
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M8 20h8M12 16v4" />
  </>,
  { linecap: 'round' },
);
export const PhoneIcon = svg(
  <>
    <rect x="7" y="2.5" width="10" height="19" rx="2" />
    <path d="M11 18.5h2" />
  </>,
  { linecap: 'round' },
);
export const TabletIcon = svg(
  <>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M11 18h2" />
  </>,
  { linecap: 'round' },
);
export const HandIcon = svg(<path d="M8 12V6a1.5 1.5 0 0 1 3 0v5M11 11V4.5a1.5 1.5 0 0 1 3 0V11M14 11V6a1.5 1.5 0 0 1 3 0v8a7 7 0 0 1-7 7 6 6 0 0 1-6-6v-2a1.5 1.5 0 0 1 3 0" />, {
  linecap: 'round',
  linejoin: 'round',
});
export const SmileIcon = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14a4 4 0 0 0 7 0M9 9.5h.01M15 9.5h.01" />
  </>,
  { linecap: 'round' },
);
export const HangUpIcon = svg(<path d="M3 14c5-4 13-4 18 0l-2 3-3-1v-2.5a11 11 0 0 0-8 0V16l-3 1z" />, { linejoin: 'round' });
export const ChatIcon = svg(<path d="M4 5h16v11H9l-5 4z" />, { linejoin: 'round' });
export const PeopleIcon = svg(
  <>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M15.5 14.2A5 5 0 0 1 21 19" />
  </>,
  { linecap: 'round' },
);
export const PlusIcon = svg(<path d="M12 5v14M5 12h14" />, { linecap: 'round', strokeWidth: 2.2 });
export const LinkIcon = svg(
  <>
    <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
    <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
  </>,
  { linecap: 'round' },
);
export const CopyIcon = svg(
  <>
    <rect x="8" y="8" width="12" height="12" rx="2" />
    <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
  </>,
  { linecap: 'round' },
);
export const PlugIcon = svg(<path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0zM12 17v4" />, { linecap: 'round', linejoin: 'round' });
export const HomeIcon = svg(<path d="M4 10l8-6 8 6v10h-5v-6H9v6H4z" />, { linejoin: 'round' });
export const RecordIcon = svg(
  <>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
export const GearIcon = svg(
  <>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
  </>,
  { linecap: 'round' },
);
export const MoreIcon = svg(
  <>
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </>,
  { fill: 'currentColor' },
);
export const FlipIcon = svg(
  <>
    <path d="M4 9a8 8 0 0 1 14-3l2 2M20 15a8 8 0 0 1-14 3l-2-2" />
    <path d="M20 4v4h-4M4 20v-4h4" />
  </>,
  { linecap: 'round', linejoin: 'round' },
);
export const ShareIcon = svg(
  <>
    <path d="M12 15V4M8 8l4-4 4 4" />
    <path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
  </>,
  { linecap: 'round', linejoin: 'round' },
);
export const CloseIcon = svg(<path d="M6 6l12 12M18 6L6 18" />, { linecap: 'round' });
export const BackIcon = svg(<path d="M15 5l-7 7 7 7" />, { linecap: 'round', linejoin: 'round' });
export const PinIcon = svg(<path d="M9 4h6l-1 6 3 3H7l3-3zM12 13v7" />, { linecap: 'round', linejoin: 'round' });
export const CalendarIcon = svg(
  <>
    <rect x="4" y="5" width="16" height="15" rx="2" />
    <path d="M8 3v4M16 3v4M4 10h16" />
  </>,
  { linecap: 'round' },
);
export const SendIcon = svg(<path d="M4 12l16-8-6 16-3-7z" />, { linejoin: 'round' });
export const LockIcon = svg(
  <>
    <rect x="5" y="11" width="14" height="9" rx="2" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </>,
  { linecap: 'round' },
);

export const MailIcon = svg(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M4 7l8 6 8-6" />
  </>,
  { linecap: 'round', linejoin: 'round' },
);
export const EyeIcon = svg(
  <>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </>,
  { linecap: 'round', linejoin: 'round' },
);
export const EyeOffIcon = svg(
  <>
    <path d="M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.2M6.5 6.6C3.7 8.4 2 12 2 12s3.6 7 10 7a9.7 9.7 0 0 0 5.4-1.6" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" />
  </>,
  { linecap: 'round', linejoin: 'round' },
);
/** A requirement met, beside a requirement still to meet: a tick, and an empty ring. */
export const CheckIcon = svg(<path d="M5 12.5l4.5 4.5L19 7.5" />, { linecap: 'round', linejoin: 'round', strokeWidth: 2.6 });
export const RingIcon = svg(<circle cx="12" cy="12" r="6.5" />, { strokeWidth: 2.2 });

export function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.2 5.500-4.800 7.200l7.600 5.900c4.500-4.100 7-10.200 7-17.600z" />
      <path fill="#FBBC05" d="M10.500 28.600A14.500 14.500 0 0 1 9.500 24c0-1.600.3-3.100.8-4.600l-7.900-6.200A24 24 0 0 0 0 24c0 3.900.9 7.500 2.600 10.800l7.900-6.200z" />
      <path fill="#34A853" d="M24 48c6.500 0 11.900-2.100 15.900-5.800l-7.600-5.900c-2.100 1.400-4.900 2.300-8.300 2.300-6.300 0-11.600-4.200-13.500-9.900l-7.900 6.200C6.500 42.600 14.600 48 24 48z" />
    </svg>
  );
}

/**
 * Apple's logo for the "Continue with Apple" button. Apple's design
 * guidelines allow only Apple's own artwork, drawn at the button's full
 * height and never cropped or redrawn. It comes under Apple's artwork
 * license, so it is added by hand (app/src/assets/sign-in-with-apple/README.md);
 * without it the button shows its title alone.
 */
const appleArtwork: string | null =
  Object.values(import.meta.glob<string>('../assets/sign-in-with-apple/logo-black.svg', { eager: true, query: '?url', import: 'default' }))[0] ?? null;

export function AppleLogo() {
  return appleArtwork ? <img className="apple-logo" src={appleArtwork} alt="" aria-hidden="true" draggable={false} /> : null;
}

export function MicrosoftLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}
